import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Typography,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  Water as UrinalysisIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { QK } from "../../../../../../base/qk";
import {
  createUrinalysis,
  deleteUrinalysisById,
  getUrinalysis,
} from "../../../../../../api/services/observations";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import EditUrinalysisModal from "./EditUrinalysisModal";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import DataTable from "../../../../../Table/DataTable";
import CustomDateTime from "../../../../../CustomForm/CustomDateTime";

const Urinalysis = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { id: clientId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedUrinalysis, setSelectedUrinalysis] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formData, setFormData] = useState({
    infection_type: "",
    action_taken: "",
    further_action_taken: "",
    date_time: new Date().toISOString().slice(0, 16),
    handover: false,
    manager: false,
  });

  const { data, refetch, isLoading } = useQuery(
    [QK.urinalysis, clientId],
    () => getUrinalysis(clientId)
  );
  const urinalysis = data?.results;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      infection_type: "",
      action_taken: "",
      further_action_taken: "",
      date_time: new Date().toISOString().slice(0, 16),
      handover: false,
      manager: false,
    });
  };

  const urinalysisPostMutation = useMutation(createUrinalysis, {
    onSuccess: () => {
      enqueueSnackbar("Urinalysis created successfully", {
        variant: "success",
      });
      queryClient.invalidateQueries([QK.urinalysis, clientId]);
      refetch();
      resetForm();
    },
    onError: (error) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to create Urinalysis",
        { variant: "error" }
      );
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      client: clientId,
    };
    urinalysisPostMutation.mutate(payload);
  };

  const deleteMutation = useMutation(deleteUrinalysisById, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.urinalysis, clientId]);
      enqueueSnackbar("Urinalysis deleted successfully", {
        variant: "success",
      });
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to delete record",
        { variant: "error" }
      );
    },
  });

  const handleDeleteClick = (id) => {
    setSelectedUrinalysis(id);
    setShowConfirmModal(true);
  };

  const handleEditClick = (id) => {
    setSelectedUrinalysis(id);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedUrinalysis(null);
  };

  const getInfectionTypeColor = (type) => {
    const colorMap = {
      "No abnormal colour": "#10B981",
      Dark: "#F59E0B",
      "Brown green/strong yellow": "#D97706",
      "Bright red/red-brown": "#EF4444",
      Clear: "#3B82F6",
    };
    return colorMap[type] || "#6B7280";
  };

  const infectionTypes = [
    { value: "No abnormal colour", label: "No abnormal colour", color: "#10B981" },
    { value: "Dark", label: "Dark", color: "#F59E0B" },
    { value: "Brown green/strong yellow", label: "Brown green/strong yellow", color: "#D97706" },
    { value: "Bright red/red-brown", label: "Bright red/red-brown", color: "#EF4444" },
    { value: "Clear", label: "Clear", color: "#3B82F6" },
  ];

  const headers = [
    { key: "date_time", label: "Date & Time" },
    { key: "infection_type", label: "Infection Type" },
    { key: "action_taken", label: "Action Taken" },
    { key: "further_action_taken", label: "Further Action" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "date_time") {
      return item.date_time ? new Date(item.date_time).toLocaleString() : "-";
    }
    if (header.key === "infection_type") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: getInfectionTypeColor(item.infection_type),
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" fontWeight={500}>
            {item.infection_type || "-"}
          </Typography>
        </Box>
      );
    }
    if (header.key === "action_taken" || header.key === "further_action_taken") {
      return (
        <span
          style={{
            display: "block",
            maxWidth: 150,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item[header.key] || "-"}
        </span>
      );
    }
    return item[header.key] || "-";
  };

  return (
    <Box>
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FAFBFC",
            borderBottom: "1px solid #F2F4F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <UrinalysisIcon
                sx={{ color: theme.palette.primary.main, fontSize: 18 }}
              />
            </Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: "#1A1A2E" }}>
              Add Urinalysis Entry
            </Typography>
            <Chip
              label="New"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 500,
                bgcolor: "#D1FAE5",
                color: "#039855",
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit} id="urinalysis-form">
            <Grid container spacing={2.5}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <CustomSelect
                  label="Infection Type (Result Color) *"
                  name="infection_type"
                  selectedValue={formData.infection_type}
                  onChange={handleChange}
                  options={infectionTypes}
                  placeholder="Select"
                  required
                  fullWidth
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <CustomInput
                  label="Action Taken"
                  name="action_taken"
                  value={formData.action_taken}
                  onChange={handleChange}
                  placeholder="Describe action taken..."
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: "text.secondary" }}
                  >
                    FURTHER TESTING
                  </Typography>
                </Divider>
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <CustomInput
                  label="Further Action Taken"
                  name="further_action_taken"
                  value={formData.further_action_taken}
                  onChange={handleChange}
                  placeholder="Details of urine sent for testing..."
                  multiline
                  rows={2}
                  fullWidth
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <CustomDateTime
                  label="Date & Time *"
                  name="date_time"
                  value={formData.date_time}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="manager"
                          checked={formData.manager}
                          onChange={handleChange}
                          sx={{
                            color: "#D0D5DD",
                            "&.Mui-checked": {
                              color: "#2469F0",
                            },
                          }}
                        />
                      }
                      label="Share with Manager"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="handover"
                          checked={formData.handover}
                          onChange={handleChange}
                          sx={{
                            color: "#D0D5DD",
                            "&.Mui-checked": {
                              color: "#2469F0",
                            },
                          }}
                        />
                      }
                      label="Handover"
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={resetForm}
                      disabled={urinalysisPostMutation.isLoading}
                      startIcon={<ClearIcon />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 500,
                        borderRadius: "8px",
                        borderColor: "#D0D5DD",
                        color: "#667085",
                        "&:hover": {
                          bgcolor: "#F9FAFB",
                          borderColor: "#98A2B3",
                        },
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={urinalysisPostMutation.isLoading}
                      startIcon={
                        urinalysisPostMutation.isLoading ? (
                          <CircularProgress size={18} />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "8px",
                        bgcolor: "#2469F0",
                        px: 4,
                        "&:hover": {
                          bgcolor: "#1A5BD4",
                          boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                        },
                      }}
                    >
                      {urinalysisPostMutation.isLoading
                        ? "Saving..."
                        : "Save Record"}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FAFBFC",
            borderBottom: "1px solid #F2F4F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: "#1A1A2E" }}>
              Urinalysis Records
            </Typography>
            <Chip
              label={urinalysis?.length || 0}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 500,
                bgcolor: "#F2F4F7",
                color: "#475467",
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 0 }}>
          <DataTable
            data={urinalysis || []}
            headers={headers}
            onUpdate={handleEditClick}
            onDelete={handleDeleteClick}
            canUpdate={true}
            canDelete={true}
            renderCell={renderCell}
            loading={isLoading}
          />
        </CardContent>
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={() => {
          if (selectedUrinalysis) {
            deleteMutation.mutate(selectedUrinalysis);
            setShowConfirmModal(false);
          }
        }}
        title="Delete Confirmation"
        body="Are you sure you want to delete this urinalysis record? This action cannot be undone."
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {selectedUrinalysis && (
        <EditUrinalysisModal
          show={showEditModal}
          handleClose={handleCloseModal}
          selectedID={selectedUrinalysis}
        />
      )}
    </Box>
  );
};

export default Urinalysis;